import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';

import {
  CustomerModal,
  InviteUserModal,
  CreateUserModal,
  DeleteUserModal,
} from 'components/Modals';
import { useQuery } from 'hooks';
import Can from 'components/Can';
import { Table } from 'components/UI';
import { useHistory } from 'react-router-dom';
import SearchIcon from '@material-ui/icons/Search';
import { usersActions } from 'state/modules/users';
import { SearchField } from 'components/FormFields';
import { UserStatusSelect } from 'components/AsyncFields';
import { StatusDetailsSelect } from 'components/DataFields';
import { useStyles, tableStyles, switchStyles } from './UsersList.styled';
import { buildUrl, capitalize, toQueryString } from 'utils';
import InputAdornment from '@material-ui/core/InputAdornment';
import {
  Box,
  Grid,
  Paper,
  Button,
  Container,
  Typography,
  FormControlLabel,
  Switch,
} from '@material-ui/core';

import { debounce } from 'lodash';
import { accountDeletionStatus } from 'constants/users';

const UsersTableList = ({ title, type, path, isCustomer }) => {
  const classes = useStyles();
  const switchClasses = switchStyles();
  const urlParams = useQuery();
  const dispatch = useDispatch();
  const history = useHistory();
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const [userId, setUserId] = useState(null);
  const { settings } = useSelector((state) => state.config || {});
  const [openDeleteUserModal, setOpenDeleteUserModal] = useState(false);
  const users = useSelector((state) => state.users.users);
  const loading = useSelector((state) => state.users.loading);
  const pagination = useSelector((state) => state.users.pagination);
  const fetchedDeleteUserSuccess = useSelector((state) => state.users.fetchedDeleteUserSuccess);
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });
  const [openCreateUserModal, setOpenCreateUserModal] = useState(false);
  const [openCreateCustomerModal, setOpenCreateCustomerModal] = useState(false);
  const [openInviteUserModal, setOpenInviteUserModal] = useState(false);

  const handleToggle = () => {
    search('deletion_status', params.deletion_status === accountDeletionStatus.REQUESTED ? '' : accountDeletionStatus.REQUESTED )
  };

  const columns = [
    {
      name: i18n.t('tableColumns.id'),
      selector: 'id',
      sortable: true,
      grow: 0,
    },
    {
      name: i18n.t('tableColumns.first_name'),
      cell: (row) => row?.first_name || '--',
      selector: 'first_name',
      sortable: true,
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.last_name'),
      cell: (row) => row?.last_name || '--',
      selector: 'last_name',
      sortable: true,
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.email'),
      cell: (row) => row?.email || '--',
      selector: 'email',
      sortable: true,
      grow: 2,
    },
    {
      name: i18n.t('tableColumns.phone'),
      cell: (row) => row?.mobile_phone || '--',
      grow: 1,
      selector: 'mobile_phone',
      sortable: true,
      omit: !isCustomer,
    },
    {
      name: i18n.t('tableColumns.role'),
      selector: (row) =>
        row.role ? i18n.t(`roles.${row?.role}`, { defaultValue: row?.role }) : '--',
      omit: isCustomer,
      grow: 2,
      style: {
        textTransform: 'capitalize',
      },
    },
    {
      name: i18n.t('tableColumns.status'),
      grow: 1,
      selector: (row) =>
        row?.status ? i18n.t(`userStatus.${row?.status}`, { defaultValue: row?.status }) : '--',
    },
    {
      name: i18n.t('common.actions_needed'),
      selector: (row) => {
        const details = row.status_details?.map((value) => {
          const status = i18n.t(`registrationStatus.${value}`, {
            defaultValue: capitalize(value?.toLowerCase()),
          });
          return status;
        });

        return details?.join(', ') || '--';
      },
      omit: !isCustomer,
      grow: 2,
    },
  ];

  const fetchUsers = useCallback(
    (params = {}) => {
      const _params = {
        ...params,
        is_customer: isCustomer,
      };
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        usersActions.fetchUsers(_params, undefined, { signal: abortController?.current?.signal })
      );
    },
    [dispatch]
  );

  const fetchUsersDebounced = useCallback(
    debounce((params) => fetchUsers(params), 500),
    [fetchUsers]
  );

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchUsersDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchUsersDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchUsers(params);
  }, []);

  // On refetch delete/update success no debounce
  useEffect(() => {
    if (fetchedDeleteUserSuccess) {
      fetchUsers(params);
    }
  }, [fetchedDeleteUserSuccess, fetchUsers]);

  const handleParams = (params) => {
    setParams(params);
    const _params = toQueryString(params);
    history.replace(buildUrl(`/${path}${_params}`));
  };

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle sort change on table
   * @param {string} column Column of the table
   * @param {string} sortDirection Sort direction
   */
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  // change number of items per page
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/${path}/${row?.id}`));
  };
  /**
   * Handle Create User Modal
   */
  const handleCreateUserModal = (userCreated) => {
    // get user list when user is created
    if (userCreated) fetchUsers(params);
    // open or close create user modal
    setOpenCreateUserModal((prevState) => !prevState);
  };
  /**
   * Handle Create Customer Modal
   */
  const handleCreateCustomerModal = (e, userCreated) => {
    // get user list when user is created
    if (userCreated) fetchUsers(params);
    // open or close create user modal
    setOpenCreateCustomerModal((prevState) => !prevState);
  };

  const handleDeleteUserModal = (id) => {
    setUserId(id);
    setOpenDeleteUserModal((prevState) => !prevState);
  };

  /**
   * Handle Invite User Modal
   */
  const handleInviteUserModal = () => {
    // open or close create user modal
    setOpenInviteUserModal((prevState) => !prevState);
  };

  // search by status, keyword and organization
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      // validate if value is an object
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach(
      (key) => !filters[key] && filters[key] !== false && delete filters[key]
    );
    handleParams({ ...filters });
  };

  return (
    <Container className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid
            container
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item>
              <Typography variant="h6" gutterBottom>
                <Box fontWeight="fontWeightBold" data-cy="title" fontSize={24} mb={1}>
                  {title}
                </Box>
              </Typography>
            </Grid>
            {type === 'employee' && (
              <Can I="create" a="user">
                <Grid item>
                  <Button
                    color="primary"
                    variant="contained"
                    data-cy="create-user-button"
                    onClick={() => handleCreateUserModal()}
                    style={{ textTransform: 'capitalize' }}
                  >
                    {i18n.t('users.create')}
                  </Button>
                </Grid>
              </Can>
            )}
            {type === 'customer' && settings?.core?.user?.create_customer_allowed && (
              <Can I="create" a="user">
                <Grid item>
                  <Button
                    color="primary"
                    variant="contained"
                    data-cy="create-user-button"
                    onClick={handleCreateCustomerModal}
                    style={{ textTransform: 'capitalize' }}
                  >
                    {i18n.t('users.create_customer')}
                  </Button>
                </Grid>
              </Can>
            )}
            {type === 'customer' && settings?.core?.user?.invite_customer_allowed && (
              <Grid item>
                <Button
                  color="primary"
                  variant="contained"
                  data-cy="create-user-button"
                  onClick={() => handleInviteUserModal()}
                  style={{ textTransform: 'capitalize' }}
                >
                  {i18n.t('users.invite')}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ paddingBottom: '25px' }}>
          <Grid
            container
            spacing={2}
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-end"
          >
            <Grid item xs={12} sm={6} md={3}>
              <SearchField
                data-cy="textfield-search"
                label={`${i18n.t('common.search')} ${type}`}
                field={{ onChange: search, name: 'keyword', value: params.keyword }}
                inputProps={{
                  startAdornment: (
                    <InputAdornment
                      classes={{
                        root: classes.searchIcon,
                      }}
                      position="start"
                    >
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Can I="read_status" a="user">
              <Grid item xs={12} sm={6} md={3}>
                <UserStatusSelect
                  form={{
                    setFieldValue: search,
                  }}
                  field={{
                    name: 'status',
                    value: params.status || '',
                  }}
                  label={i18n.t('common.filter_by_status')}
                />
              </Grid>
            </Can>
            <Grid item xs={12} sm={6} md={3}>
              <StatusDetailsSelect
                form={{
                  setFieldValue: search,
                }}
                omitFields={['customers', 'all']}
                field={{
                  name: 'status_detail',
                  value: params.status_detail || '',
                }}
                label={i18n.t('common.status_details')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.switchWrapper}>
              <FormControlLabel
                value="start"
                classes={switchClasses}
                control={
                  <Switch
                    data-cy="switch-show-only-deletion-requested" 
                    color="primary"
                    checked={params.deletion_status === accountDeletionStatus.REQUESTED}
                    onChange={handleToggle}
                    inputProps={{ 'aria-label': i18n.t('users.show_only_deletion_requested') }}
                  />
                }
                label={i18n.t('users.show_only_deletion_requested')}
                labelPlacement="start"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Paper elevation={2} className={classes.paper}>
            <Table
              data-cy="table-users"
              data={users}
              columns={columns}
              loading={loading}
              page={params.page}
              onSort={handleSort}
              limit={params.limit}
              extraStyles={{ ...tableStyles }}
              handleRowClicked={handleRowClick}
              handlePageChange={handlePageChange}
              defaultSortAsc={!params?.sort_desc}
              defaultSortField={params?.sort_field}
              totalRows={pagination?.totalRows || 0}
              handlePerRowsChange={handlePerRowsChange}
            />
          </Paper>
        </Grid>
        {openCreateUserModal && <CreateUserModal open={true} handleClick={handleCreateUserModal} />}
        {openCreateCustomerModal && (
          <CustomerModal open={openCreateCustomerModal} handleClick={handleCreateCustomerModal} />
        )}
        {openDeleteUserModal && (
          <DeleteUserModal
            id={userId}
            type={type}
            open={openDeleteUserModal}
            handleClick={handleDeleteUserModal}
          />
        )}
        {openInviteUserModal && (
          <InviteUserModal
            open={true}
            handleClick={handleInviteUserModal}
            onSuccess={() => fetchUsers(params)}
          />
        )}
      </Grid>
    </Container>
  );
};

UsersTableList.propTypes = {
  history: PropTypes.any,
  title: PropTypes.string.isRequired,
  isCustomer: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(['customer', 'employee']).isRequired,
};

UsersTableList.defaultProps = {
  title: '',
  type: 'employee',
  isCustomer: false,
};

export default UsersTableList;
